import { useEffect, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import { renderLoginSticker } from "@/lib/qrLabel";
import {
  isBluetoothSupported, isPrinterConnected, connectPrinter, reconnectPrinter, printCanvas,
} from "@/lib/btPrinter";
import {
  Palette, Building2, Upload, ImageIcon, Plus, Pencil, Trash2,
  Star, X, Eye, EyeOff, Check, QrCode, Printer, Download, RotateCw, Save, Bluetooth, Loader2,
} from "lucide-react";

const CUSTOMER_LOGIN_URL = "https://compliance.vanguardbeacon.com/";
const LOGIN_LINES = ["Scan to log in", "compliance.vanguardbeacon.com", "Vanguard Beacon"];

// ---------- Customer login QR (configurable size, rotation, thermal print) ----------
function CustomerLoginQR() {
  const { notify } = useDialog();
  const canvasRef = useRef(null);
  const [w, setW] = useState(80);
  const [h, setH] = useState(60);
  const [gap, setGap] = useState(2);
  const [rotate, setRotate] = useState(false);
  const [qty, setQty] = useState(1);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/food-prep-config").then(({ data }) => {
      setCfg(data);
      setW(data.qr_label_width_mm || 80);
      setH(data.qr_label_height_mm || 60);
      setGap(data.qr_label_gap_mm != null ? data.qr_label_gap_mm : 2);
      setRotate(!!data.qr_label_rotate);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;
    renderLoginSticker(canvasRef.current, CUSTOMER_LOGIN_URL, LOGIN_LINES,
      { widthMm: Number(w) || 80, heightMm: Number(h) || 60, dpi: 203, rotate }).catch(() => {});
  }, [w, h, rotate]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/food-prep-config", {
        qr_label_width_mm: Number(w), qr_label_height_mm: Number(h),
        qr_label_gap_mm: Number(gap), qr_label_rotate: rotate,
      });
      setCfg(data);
      await notify({ title: "QR label size saved", message: "Applied to Site, Equipment & Login QR stickers.", variant: "ok" });
    } catch (e) { await notify({ title: "Save failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
    finally { setSaving(false); }
  };

  const download = () => {
    const c = canvasRef.current;
    if (!c) return;
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png");
    a.download = "vanguard-beacon-login-qr.png";
    a.click();
  };

  const printThermal = async () => {
    if (!isBluetoothSupported()) {
      await notify({ title: "Bluetooth not available", message: "Direct printing needs Chrome on an Android device.", variant: "err" });
      return;
    }
    setBusy(true);
    try {
      const printCfg = {
        ...(cfg || {}), label_width_mm: Number(w), label_height_mm: Number(h), label_dpi: 203,
        printer_command: (cfg && cfg.printer_command) || "tspl",
        printer_gap_mm: Number(gap),
      };
      if (!isPrinterConnected()) {
        const ok = await reconnectPrinter(printCfg);
        if (!ok) await connectPrinter(printCfg);
      }
      await printCanvas(canvasRef.current, printCfg, Math.max(1, parseInt(qty, 10) || 1));
      await notify({ title: `Sent ${Math.max(1, parseInt(qty, 10) || 1)} sticker(s) to printer`, variant: "ok" });
    } catch (e) { await notify({ title: "Print failed", message: e.message || "Could not print.", variant: "err" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="vb-card p-6" data-testid="customer-login-qr">
      <h2 className="vb-heading text-lg flex items-center gap-2 mb-1" style={{ color: "var(--vb-text-2)" }}>
        <QrCode size={18} /> QR sticker label size
      </h2>
      <p className="text-xs opacity-60 max-w-xl mb-4">
        These dimensions apply to <strong>all QR stickers</strong> printed on the thermal printer —
        Site, Equipment and the Customer login QR (below). The login QR opens the login page
        (<a href={CUSTOMER_LOGIN_URL} target="_blank" rel="noreferrer" style={{ color: "var(--vb-text-2)" }}>{CUSTOMER_LOGIN_URL}</a>)
        when scanned. Set the size + gap once; it is used everywhere QR labels are printed.
      </p>
      <div className="flex items-start gap-6 flex-wrap">
        <div className="vb-card flex items-center justify-center" style={{ padding: 10, background: "#fff" }}>
          <canvas ref={canvasRef} style={{ width: 160, height: "auto", maxHeight: 200, background: "#fff" }} data-testid="customer-login-qr-canvas" />
        </div>
        <div className="flex flex-col gap-3" style={{ minWidth: 260 }}>
          <div className="flex items-end gap-3 flex-wrap">
            <label className="text-sm">
              <span className="block mb-1 font-medium">Width (mm)</span>
              <input className="vb-input" type="number" min="15" max="150" value={w} style={{ width: 90 }}
                onChange={(e) => setW(e.target.value)} data-testid="login-qr-width" />
            </label>
            <label className="text-sm">
              <span className="block mb-1 font-medium">Height (mm)</span>
              <input className="vb-input" type="number" min="15" max="150" value={h} style={{ width: 90 }}
                onChange={(e) => setH(e.target.value)} data-testid="login-qr-height" />
            </label>
            <label className="text-sm">
              <span className="block mb-1 font-medium">Gap (mm)</span>
              <input className="vb-input" type="number" min="0" max="20" step="0.5" value={gap} style={{ width: 90 }}
                onChange={(e) => setGap(e.target.value)} data-testid="login-qr-gap" />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={rotate} onChange={(e) => setRotate(e.target.checked)}
              data-testid="login-qr-rotate" style={{ width: 16, height: 16 }} />
            <RotateCw size={14} /> Rotate 90° (fits a bigger QR on wide labels)
          </label>
          <button className="vb-btn ghost sm w-fit" disabled={saving} onClick={save} data-testid="login-qr-save">
            <Save size={13} /> {saving ? "Saving…" : "Save label size"}
          </button>

          <div className="flex items-center gap-2 mt-1">
            <label className="text-xs opacity-70">Qty</label>
            <input type="number" min="1" value={qty} className="vb-input" style={{ width: 64, padding: "4px 8px" }}
              onChange={(e) => setQty(e.target.value)} data-testid="login-qr-qty" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button className="vb-btn" disabled={busy} onClick={printThermal} data-testid="customer-login-qr-print">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Bluetooth size={14} />} Print login QR (thermal)
            </button>
            <button className="vb-btn ghost" onClick={download} data-testid="customer-login-qr-download"><Download size={14} /> Download PNG</button>
          </div>
          <span className="text-xs opacity-60" style={{ maxWidth: 260 }}>Tip: laminate and place at reception or in the kitchen for quick staff access.</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Platform brand settings (super-admin only).
//
// Two stacked sections on a single page:
//   1. Company profile      — Vanguard Beacon's own legal identity / logo
//   2. Brand-theme catalog  — the 3-slot curated palette tenants pick from
//                             (replaces the old free-form "Default color
//                             palette" which let admins type any hex value).
//   3. Typography           — platform-wide heading + body fonts (carried over
//                             from the legacy brand settings).
// ---------------------------------------------------------------------------

// ---------- Logo upload (platform-profile endpoint) ----------
function PlatformLogoUpload({ currentUrl, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/platform-profile/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.url);
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const displaySrc = currentUrl ? `${currentUrl}${currentUrl.includes("?") ? "&" : "?"}v=${Date.now()}` : null;

  return (
    <div>
      <Label>Platform logo</Label>
      <div className="flex items-center gap-3 flex-wrap">
        <div
          className="vb-card flex items-center justify-center"
          style={{ width: 120, height: 120, background: "var(--vb-bg-2)", overflow: "hidden" }}
          data-testid="platform-logo-preview"
          key={displaySrc}
        >
          {displaySrc
            ? <img src={displaySrc} alt="Vanguard Beacon logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            : <ImageIcon size={36} className="opacity-40" />}
        </div>
        <div className="flex flex-col gap-1">
          <input
            ref={inputRef} type="file"
            accept=".png,.jpg,.jpeg,.gif,.svg,.webp,image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
            className="hidden" onChange={onPick}
            data-testid="platform-logo-input"
          />
          <button
            type="button" className="vb-btn ghost"
            onClick={() => inputRef.current?.click()}
            disabled={busy} data-testid="platform-logo-upload"
          >
            <Upload size={14} /> {busy ? "Uploading…" : (currentUrl ? "Replace logo" : "Upload logo")}
          </button>
          <span className="text-xs opacity-60">PNG, JPG, GIF, SVG or WEBP · recommended 200×200 px · max 4 MB</span>
          {err && <span className="text-xs" style={{ color: "var(--vb-error)" }}>{err}</span>}
        </div>
      </div>
    </div>
  );
}


// ---------- Theme catalog ----------
const PALETTE_KEYS = [
  ["primary_bg",     "Primary background"],
  ["secondary_bg",   "Secondary background"],
  ["primary_text",   "Primary text"],
  ["secondary_text", "Secondary text / accents"],
  ["accent",         "Accent"],
  ["warning",        "Warning"],
  ["error",          "Error"],
];

const blankTheme = () => ({
  name: "", description: "",
  palette: {
    primary_bg: "#E7D7C8", secondary_bg: "#e2c8b5",
    primary_text: "#2f2e2c", secondary_text: "#783D19",
    accent: "#2e630c", warning: "#f97316", error: "#ef4444",
  },
  is_active: true, is_default: false, sort_order: 100,
});

function ThemeCatalog() {
  const dialog = useDialog();
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/themes");
      setThemes(data.themes || []);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to load themes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setDefault = async (t) => {
    if (!await dialog.confirm({
      title: `Make "${t.name}" the default theme?`,
      message: "Brand-new tenants will land on this theme. Existing tenants are unaffected.",
      confirmLabel: "Set as default",
    })) return;
    try { await api.post(`/admin/themes/${t.id}/set-default`); load(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const toggleActive = async (t) => {
    try { await api.patch(`/admin/themes/${t.id}`, { is_active: !t.is_active }); load(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const del = async (t) => {
    if (!await dialog.confirm({
      title: `Delete theme "${t.name}"?`,
      message: t.tenant_count > 0
        ? `${t.tenant_count} tenant(s) are using this theme. Deletion will be refused — deactivate it instead.`
        : "This action cannot be undone.",
      danger: true, confirmLabel: "Delete theme",
    })) return;
    try { await api.delete(`/admin/themes/${t.id}`); load(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className="vb-card p-6" data-testid="theme-catalog">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div>
          <h2 className="vb-heading text-lg flex items-center gap-2 mb-1" style={{ color: "var(--vb-text-2)" }}>
            <Palette size={18} /> Brand-theme catalog
          </h2>
          <p className="text-xs opacity-60 max-w-xl">
            Curated palette catalog tenants pick from in their workspace settings. Exactly one theme should be marked Default — new tenants land on it automatically.
          </p>
        </div>
        <button className="vb-btn" onClick={() => setModal({ mode: "add", form: blankTheme() })} data-testid="catalog-add-theme">
          <Plus size={14} /> New theme
        </button>
      </div>

      {err && <div className="vb-pill err mt-3" data-testid="catalog-err">{err}</div>}

      {loading ? (
        <div className="opacity-70 text-sm mt-4">Loading themes…</div>
      ) : themes.length === 0 ? (
        <div className="opacity-60 text-sm mt-4">No themes yet. Click "New theme" to create the first one.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4" data-testid="catalog-grid">
          {themes.map((t) => (
            <ThemeCard
              key={t.id} theme={t}
              onEdit={() => setModal({ mode: "edit", id: t.id, form: { ...t } })}
              onSetDefault={() => setDefault(t)}
              onToggleActive={() => toggleActive(t)}
              onDelete={() => del(t)}
            />
          ))}
        </div>
      )}

      {modal && (
        <ThemeModal
          mode={modal.mode} id={modal.id} initial={modal.form}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function ThemeCard({ theme: t, onEdit, onSetDefault, onToggleActive, onDelete }) {
  const p = t.palette || {};
  return (
    <div className="vb-card p-0 overflow-hidden border" data-testid={`catalog-theme-${t.id}`}>
      <div className="flex" style={{ height: 48 }}>
        <div style={{ flex: 2, background: p.primary_bg }} />
        <div style={{ flex: 1, background: p.secondary_bg }} />
        <div style={{ flex: 1, background: p.accent }} />
        <div style={{ flex: 1, background: p.warning }} />
        <div style={{ flex: 1, background: p.error }} />
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="min-w-0">
            <div className="vb-heading text-base m-0 truncate">{t.name}</div>
            <div className="flex gap-1 flex-wrap mt-1">
              {t.is_default && <span className="vb-pill ok text-[10px]"><Star size={10} className="inline -mt-0.5 mr-0.5" /> Default</span>}
              <span className={`vb-pill text-[10px] ${t.is_active ? "ok" : ""}`}>
                {t.is_active ? "Active" : "Hidden"}
              </span>
              <span className="vb-pill text-[10px]">{t.tenant_count || 0} tenant{t.tenant_count === 1 ? "" : "s"}</span>
            </div>
          </div>
          <div className="flex gap-1">
            <button className="vb-btn ghost" onClick={onEdit} title="Edit" data-testid={`catalog-theme-edit-${t.id}`}><Pencil size={13} /></button>
            <button className="vb-btn ghost" onClick={onDelete} title="Delete" data-testid={`catalog-theme-delete-${t.id}`}><Trash2 size={13} /></button>
          </div>
        </div>
        <p className="text-xs opacity-70 m-0 mb-2" style={{ minHeight: 36 }}>{t.description}</p>
        <div className="flex gap-2 flex-wrap">
          {!t.is_default && (
            <button className="vb-btn ghost text-xs" onClick={onSetDefault} data-testid={`catalog-theme-default-${t.id}`}>
              <Star size={12} /> Make default
            </button>
          )}
          <button className="vb-btn ghost text-xs" onClick={onToggleActive} data-testid={`catalog-theme-toggle-${t.id}`}>
            {t.is_active ? <><EyeOff size={12} /> Deactivate</> : <><Eye size={12} /> Activate</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ThemeModal({ mode, id, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPal = (k, v) => setForm((f) => ({ ...f, palette: { ...f.palette, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = {
        name: form.name, description: form.description,
        palette: form.palette, is_active: !!form.is_active,
        is_default: !!form.is_default, sort_order: Number(form.sort_order) || 100,
      };
      if (mode === "add") await api.post("/admin/themes", payload);
      else await api.patch(`/admin/themes/${id}`, payload);
      onSaved();
    } catch (ex) { setErr(formatErr(ex.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="vb-modal-bg" onClick={onClose} data-testid="catalog-theme-modal-bg">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }} data-testid="catalog-theme-modal">
        <div className="flex items-start justify-between mb-3">
          <h2 className="vb-heading text-xl m-0">{mode === "add" ? "New theme" : "Edit theme"}</h2>
          <button className="vb-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        {err && <div className="vb-pill err mb-3" data-testid="catalog-theme-modal-err">{err}</div>}
        <form onSubmit={submit} className="space-y-3" data-testid="catalog-theme-form">
          <div>
            <Label>Name *</Label>
            <input className="vb-input" required value={form.name}
              onChange={(e) => set("name", e.target.value)} data-testid="catalog-theme-name" />
          </div>
          <div>
            <Label>Description</Label>
            <textarea className="vb-input" rows={3} value={form.description}
              onChange={(e) => set("description", e.target.value)} data-testid="catalog-theme-desc" />
          </div>
          <div>
            <Label>Palette</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PALETTE_KEYS.map(([k, label]) => (
                <div key={k}>
                  <label className="text-[10px] opacity-70">{label}</label>
                  <div className="flex items-center gap-2">
                    <input type="color"
                      value={form.palette[k] || "#000000"}
                      onChange={(e) => setPal(k, e.target.value)}
                      style={{ width: 42, height: 34, border: "1px solid var(--vb-border)", borderRadius: 6 }}
                      data-testid={`catalog-theme-color-${k}`} />
                    <input className="vb-input text-xs" value={form.palette[k] || ""}
                      onChange={(e) => setPal(k, e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => set("is_active", e.target.checked)} data-testid="catalog-theme-active" />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.is_default} onChange={(e) => set("is_default", e.target.checked)} data-testid="catalog-theme-default" />
              Default
            </label>
            <div>
              <label className="text-[10px] opacity-70">Sort order</label>
              <input className="vb-input text-xs" type="number" value={form.sort_order || 100}
                onChange={(e) => set("sort_order", e.target.value)} data-testid="catalog-theme-sort" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="vb-btn ghost" onClick={onClose} data-testid="catalog-theme-cancel">Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy} data-testid="catalog-theme-submit">
              {busy ? "Saving…" : <><Check size={14} /> Save theme</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ---------- Main page ----------
export default function BrandSettings() {
  const [branding, setBranding] = useState({});
  const [profile, setProfile] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [brandBusy, setBrandBusy] = useState(false);
  const [profileBusy, setProfileBusy] = useState(false);
  const [brandMsg, setBrandMsg] = useState("");
  const [brandErr, setBrandErr] = useState("");
  const [profileMsg, setProfileMsg] = useState("");
  const [profileErr, setProfileErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [b, p] = await Promise.all([
          api.get("/admin/platform-branding"),
          api.get("/admin/platform-profile"),
        ]);
        setBranding(b.data.platform_branding || {});
        setProfile(p.data.platform_profile || {});
      } catch (e) { setBrandErr(formatErr(e.response?.data?.detail)); }
      finally { setLoaded(true); }
    })();
  }, []);

  const updBranding = (k) => (e) => setBranding({ ...branding, [k]: e.target.value });
  const updProfile = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  const saveTypography = async (e) => {
    e.preventDefault(); setBrandBusy(true); setBrandMsg(""); setBrandErr("");
    try {
      const { id: _id, ...payload } = branding;
      void _id;
      const { data } = await api.put("/admin/platform-branding", payload);
      setBranding(data.platform_branding);
      setBrandMsg("Platform typography saved.");
    } catch (e) { setBrandErr(formatErr(e.response?.data?.detail)); }
    finally { setBrandBusy(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault(); setProfileBusy(true); setProfileMsg(""); setProfileErr("");
    try {
      const payload = {
        legal_company_name: profile.legal_company_name || "",
        tagline: profile.tagline || "",
        address: profile.address || "",
        contact_email: profile.contact_email || "",
        logo_url: profile.logo_url || "",
      };
      const { data } = await api.put("/admin/platform-profile", payload);
      setProfile(data.platform_profile);
      setProfileMsg("Platform company profile saved.");
    } catch (e) { setProfileErr(formatErr(e.response?.data?.detail)); }
    finally { setProfileBusy(false); }
  };

  const onLogoUploaded = (url) => setProfile((p) => ({ ...p, logo_url: url }));

  if (!loaded) return <div data-testid="brand-settings-loading">Loading…</div>;

  return (
    <div className="vb-fade-in max-w-5xl space-y-6" data-testid="brand-settings-page">
      <div>
        <h1 className="vb-heading text-3xl mb-1 flex items-center gap-2" style={{ color: "var(--vb-text-2)" }}>
          <Palette size={26} /> Platform brand settings
        </h1>
        <p className="opacity-70">
          Manage Vanguard Beacon's own identity and the curated brand-theme catalog every tenant picks from.
        </p>
      </div>

      {/* ---------- Platform company profile ---------- */}
      <form onSubmit={saveProfile} className="vb-card p-6 grid gap-5" data-testid="platform-profile-form">
        <div>
          <h2 className="vb-heading text-lg flex items-center gap-2 mb-1" style={{ color: "var(--vb-text-2)" }}>
            <Building2 size={18} /> Company profile
          </h2>
          <p className="text-xs opacity-60">Used on auditor-facing reports, the public footer, and the login screen.</p>
        </div>

        <PlatformLogoUpload currentUrl={profile.logo_url} onUploaded={onLogoUploaded} />

        <div>
          <Label>Company name</Label>
          <input className="vb-input" value={profile.legal_company_name || ""} onChange={updProfile("legal_company_name")} placeholder="e.g. Vanguard Beacon Pte Ltd" data-testid="platform-profile-name" />
        </div>
        <div>
          <Label>Tagline</Label>
          <input className="vb-input" value={profile.tagline || ""} onChange={updProfile("tagline")} placeholder="Audit-ready, every day" data-testid="platform-profile-tagline" />
        </div>
        <div>
          <Label>Company address</Label>
          <textarea className="vb-input" rows={2} value={profile.address || ""} onChange={updProfile("address")} placeholder="Registered address" data-testid="platform-profile-address" />
        </div>
        <div>
          <Label>Contact email</Label>
          <input className="vb-input" type="email" value={profile.contact_email || ""} onChange={updProfile("contact_email")} placeholder="hello@vanguardbeacon.com" data-testid="platform-profile-email" />
        </div>

        {profileMsg && <div className="vb-pill ok" data-testid="platform-profile-msg">{profileMsg}</div>}
        {profileErr && <div className="vb-pill err" data-testid="platform-profile-err">{profileErr}</div>}
        <button type="submit" className="vb-btn self-start" disabled={profileBusy} data-testid="platform-profile-save">
          {profileBusy ? "Saving…" : "Save company profile"}
        </button>
      </form>

      {/* ---------- Customer login QR code ---------- */}
      <CustomerLoginQR />

      {/* ---------- Theme catalog (replaces the old Default color palette) ---------- */}
      <ThemeCatalog />

      {/* ---------- Typography (platform-wide) ---------- */}
      <form onSubmit={saveTypography} className="vb-card p-6 grid gap-5" data-testid="platform-typography-form">
        <div>
          <h2 className="vb-heading text-lg mb-1" style={{ color: "var(--vb-text-2)" }}>Typography (platform-wide)</h2>
          <p className="text-xs opacity-60">Use any Google Font name. Applies to every tenant — customers cannot override.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Heading font</Label>
            <input className="vb-input" value={branding.heading_font || ""} onChange={updBranding("heading_font")} placeholder="Fraunces" data-testid="pf-heading-font" />
          </div>
          <div>
            <Label>Body font</Label>
            <input className="vb-input" value={branding.body_font || ""} onChange={updBranding("body_font")} placeholder="Catamaran" data-testid="pf-body-font" />
          </div>
        </div>

        {brandMsg && <div className="vb-pill ok" data-testid="platform-brand-msg">{brandMsg}</div>}
        {brandErr && <div className="vb-pill err" data-testid="platform-brand-err">{brandErr}</div>}
        <button type="submit" className="vb-btn self-start" disabled={brandBusy} data-testid="pf-save">
          {brandBusy ? "Saving…" : "Save typography"}
        </button>
      </form>
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>
      {children}
    </label>
  );
}
